import type { MeDto, Permission, Role } from "@shoppy/shared";
import { createContext, useContext, useMemo } from "react";

/**
 * Who is signed in, and what they may do.
 *
 * The console never decides authorisation — the api does, and it is the only
 * side that can. What this module is for is **not drawing a verb the operator
 * cannot exercise** (§7.3): the hand cursor over a row whose panel is a 403 for
 * the person looking at it is the engine's own defect arriving through the door
 * marked authorisation.
 */

export type SessionState =
  | { readonly status: "loading" }
  /** No session, or the api forgot it. The shell routes to sign-in. */
  | { readonly status: "anon" }
  | { readonly status: "unreachable" }
  | { readonly status: "ready"; readonly me: MeDto };

export interface SessionValue {
  readonly state: SessionState;
  readonly refresh: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession outside SessionProvider");
  return value;
}

/**
 * The signed-in operator, or `null`. Screens behind the shell can assume the
 * first; the shell is what guarantees it.
 */
export function useOperator(): MeDto | null {
  const { state } = useSession();
  return state.status === "ready" ? state.me : null;
}

export interface Capabilities {
  readonly can: (permission: Permission) => boolean;
  readonly hasRole: (role: Role) => boolean;
  /**
   * Whether this session was opened with a passkey.
   *
   * ADR-0013 §2 requires one for ADMIN and FINANCE, and the api refuses every
   * handler without it — except the enrolment endpoints, which are marked
   * `@AllowEnrolment()` so a new admin can register their first passkey. So an
   * OTP-backed admin session legitimately exists, reaches `/v1/me`, and can do
   * nothing else. The shell sends such a session to the enrolment screen rather
   * than drawing a console whose every register 403s.
   */
  readonly passkeyBacked: boolean;
}

export function useCapabilities(): Capabilities {
  const me = useOperator();
  return useMemo(() => {
    const permissions = new Set<string>(me?.permissions ?? []);
    const roles = new Set<string>(me?.account.roles ?? []);
    return {
      can: (permission: Permission) => permissions.has(permission),
      hasRole: (role: Role) => roles.has(role),
      passkeyBacked: me?.session.method === "PASSKEY",
    };
  }, [me]);
}
